import {UserActionType} from "./types/UserAction";
import Controller from "./controller/Controller";
import {Charge} from "./types/response/Charge";
import {retryWhile} from "../utils/retryWhile";
import {ChargeStatus} from "./types/enums/ChargeStatus";
import {ChargeFlowActionType} from "./types/enums/ChargeFlowActionType";
import {ErrorResponse} from "./types/response/ErrorResponse";
import {ServerResultHandler} from "./handleServerResult";
import {PaymentMethod} from "./types/response/PaymentMethod";
import {PaymentMethodStatus} from "./types/enums/PaymentMethodStatus";
import {PaymentMethodFlowActionType} from "./types/enums/PaymentMethodFlowActionType";
import {FlowingObject} from "./types/response/FlowingObject";
import {timeoutPromise} from "../utils/timeoutPromise";
import ApmModal from "./apm/ApmModal";

type HandleChargeNextActionParamsActions = {
    redirect?: 'auto' | 'disabled',
    wait?: 'auto' | 'disabled',
    qr_code?: 'auto' | 'disabled',
    app_redirect?: 'auto' | 'disabled',
    mobile_app_confirmation?: 'auto' | 'disabled',
};

export interface HandleChargeNextActionParams {
    signal?: AbortSignal
    actions?: HandleChargeNextActionParamsActions
}

const HandleChargeNextActionParamsDefaults: HandleChargeNextActionParams = {
    actions: {
        redirect: 'auto',
        wait: 'auto',
        app_redirect: 'auto',
        qr_code: 'auto',
        mobile_app_confirmation: 'auto',
    }
}

const POLLING_TIMEOUT = 1000;

export class FlowNextActionHandler {
    constructor(
        private readonly _defaultController: Controller,
        private readonly _serverResultHandler: ServerResultHandler,
        private readonly _apmModal?: ApmModal,
    ) {
    }

    handleChargeNextAction(subject: string | Charge, params: HandleChargeNextActionParams): Promise<Charge> {
        const chargePromise = (typeof subject === 'string')
            ? this._defaultController.action(UserActionType.CHARGE_RETRIEVE, {clientObjectId: subject})
                .then(result => this.sendToTracker(UserActionType.CHARGE_RETRIEVE, subject, result))
            : Promise.resolve(subject);

        return chargePromise
            .then(charge => {
                if (charge?.status === ChargeStatus.pending) {
                    return this.handlePendingChargeNextAction(charge.clientObjectId, params, charge);
                }
                return charge;
            });
    }

    private handlePendingChargeNextAction(clientObjectId: string, params: HandleChargeNextActionParams, charge: Charge, ): Promise<Charge> {
        const requestedActions = {...HandleChargeNextActionParamsDefaults.actions, ...(params?.actions || {})};
        const flowNextAction = charge.flow?.nextAction;

        let result: Promise<Charge>;
        switch (flowNextAction) {
            case "redirect":
                result = this.handleRedirectAction(requestedActions, clientObjectId, params, charge);
                break;
            case "app_redirect":
                result = this.handleRedirectAppAction(requestedActions, clientObjectId, params, charge);
                break;
            case "qr_code":
                result = this.handleQrCodeAction(requestedActions, clientObjectId, params, charge);
                break;
            case "mobile_app_confirmation":
                result = this.handleMobileAppConfirmationAction(requestedActions, clientObjectId, params, charge);
                break;
            case "wait":
                result = this.handleWaitAction(requestedActions, clientObjectId, params, charge);
                break;
            default:
                result = Promise.resolve(charge);
                break;
        }

        return result;
    }

    private handleRedirectAction(requestedActions: HandleChargeNextActionParamsActions, clientObjectId: string, params: HandleChargeNextActionParams, charge: Charge): Promise<Charge> {
        if (requestedActions.redirect === 'auto') {
            window.location.href = charge.flow.redirect?.redirectUrl;
            return new Promise(() => {
                // we are already redirecting => never resolve
            });
        }
        return Promise.resolve(charge);
    }

    private handleRedirectAppAction(requestedActions: HandleChargeNextActionParamsActions, clientObjectId: string, params: HandleChargeNextActionParams, charge: Charge): Promise<Charge> {
        if (requestedActions.app_redirect === 'auto') {
            window.open(charge.flow.redirect?.redirectUrl, "_blank").focus();
            return timeoutPromise(POLLING_TIMEOUT).then(() => this.pollAndHandleUpdatedChargeState(clientObjectId, params, ChargeFlowActionType.app_redirect, undefined));
        }
        return Promise.resolve(charge);
    }

    private handleQrCodeAction(requestedActions: HandleChargeNextActionParamsActions, clientObjectId: string, params: HandleChargeNextActionParams, charge: Charge): Promise<Charge> {
        if (requestedActions.qr_code === 'auto') {
            const controller = new AbortController();
            const signal = controller.signal;
            const { close } = this._apmModal?.showQrCode({
                qrCodeSrc: charge.flow.qrCode?.imgSrc,
                charge: {
                    amount: charge.amount,
                    currency: charge.currency,
                },
                paymentMethodType: charge.paymentMethod.type
            }, {
                onClose: () => {
                    controller.abort();
                }
            });
            return this.pollAndHandleUpdatedChargeState(clientObjectId, params, ChargeFlowActionType.qr_code, close, signal);
        }
        return Promise.resolve(charge);
    }

    private handleMobileAppConfirmationAction(requestedActions: HandleChargeNextActionParamsActions, clientObjectId: string, params: HandleChargeNextActionParams, charge: Charge): Promise<Charge> {
        if (requestedActions.mobile_app_confirmation === 'auto') {
            const controller = new AbortController();
            const signal = controller.signal;
            const { close } = this._apmModal?.showAcceptApp({
                charge: {
                    amount: charge.amount,
                    currency: charge.currency,
                },
                paymentMethodType: charge.paymentMethod.type
            }, {
                onClose: () => {
                    controller.abort();
                }
            });

            return this.pollAndHandleUpdatedChargeState(clientObjectId, params, ChargeFlowActionType.mobile_app_confirmation, close, signal);
        }
        return Promise.resolve(charge);
    }

    private handleWaitAction(requestedActions: HandleChargeNextActionParamsActions, clientObjectId: string, params: HandleChargeNextActionParams, charge: Charge): Promise<Charge> {
        if (requestedActions.wait === 'auto') {
            return timeoutPromise(POLLING_TIMEOUT).then(() => this.pollAndHandleUpdatedChargeState(clientObjectId, params, ChargeFlowActionType.wait, undefined));
        }
        return Promise.resolve(charge);
    }

    private pollAndHandleUpdatedChargeState(clientObjectId: string, params: HandleChargeNextActionParams, currentAction: ChargeFlowActionType, callback: Function, signal?: AbortSignal): Promise<Charge> {
        if (signal?.aborted) {
            return Promise.reject(new DOMException('Aborted', 'AbortError'));
        }

        const polling = this._defaultController.action(UserActionType.CHARGE_RETRIEVE, { clientObjectId })
            .then(result => this.sendToTracker(UserActionType.CHARGE_RETRIEVE, clientObjectId, result))
            .then(charge => {
                if (charge?.status !== ChargeStatus.pending || charge.flow?.nextAction !== currentAction) {
                    if (callback) {
                        callback();
                    }
                    return this.handleChargeNextAction(charge, params);
                }
                return timeoutPromise(POLLING_TIMEOUT)
                    .then(() => this.pollAndHandleUpdatedChargeState(clientObjectId, params, currentAction, callback, signal));
            });

        if (signal) {
            return Promise.race([
                polling,
                new Promise<Charge>((_, reject) => {
                    signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), { once: true });
                })
            ]);
        }
        return polling;
    }

    handlePaymentMethodNextAction(clientObjectId: string, params: { signal?: AbortSignal }): Promise<PaymentMethod> {
        const hasNextActionWait = (paymentMethod: PaymentMethod) => {
            return paymentMethod?.status === PaymentMethodStatus.pending
                && paymentMethod?.flow?.nextAction === PaymentMethodFlowActionType.wait;
        };
        const action = () => this._defaultController.action(UserActionType.PAYMENT_METHOD_RETRIEVE, {clientObjectId})
            .then(object => this.sendToTracker(UserActionType.PAYMENT_METHOD_RETRIEVE, clientObjectId, object));

        return retryWhile(action, hasNextActionWait, POLLING_TIMEOUT, params);
    }

    private sendToTracker<RES extends FlowingObject>(
        actionType: UserActionType,
        clientObjectId: string,
        result: RES | ErrorResponse
    ): Promise<RES> {
        return this._serverResultHandler.handle(`handle_next_action.${actionType}`, result,
            obj => ({clientObjectId, status: obj.status, nextAction: obj.flow && obj.flow.nextAction}),
            res => ({clientObjectId, error: res.error})
        )
    }
}
